import Phaser from 'phaser';
import { HP_DEFAULT, SWORD_OFFSET, TILE_SIZE, BUBBLE_COUNT, MAX_AIR_TICKS, TICKS_PER_BUBBLE, TileType } from '@blockworld/shared';
import type { ProcessedTextures } from '../rendering/TextureManager';
import { MICRO_FONT_KEY } from '../rendering/PixelFont';
import { atlasCreateImage, atlasApplyTexture } from '../rendering/AtlasUtils';

// ============================================================
// COMBAT HUD
// Manages heart display (HP), bubble display (air/breathing),
// and sword sprite rendering for both the local player and
// other players.
// ============================================================

export class CombatHUD {
    private heartSprites: Phaser.GameObjects.Image[] = [];
    private bubbleSprites: Phaser.GameObjects.Image[] = [];
    private swordSprite!: Phaser.GameObjects.Image;
    private otherPlayerSwords: Map<string, Phaser.GameObjects.Image> = new Map();
    private bubblesVisible: boolean = false;
    private equipmentBox!: Phaser.GameObjects.Graphics;
    private equipmentSprite: Phaser.GameObjects.Image | null = null;
    private currentEquippedItem: TileType | null = null;
    private serverLoadDot: Phaser.GameObjects.Graphics | null = null;
    private serverLoadLevel: number = 0;
    private serverLoadBlinkTimer: number = 0;

    constructor(
        private scene: Phaser.Scene,
        private processedTextures: ProcessedTextures,
    ) {}

    /**
     * Create initial heart sprites and the local sword sprite.
     * Call once during scene create().
     */
    create(): void {
        // Heart sprites
        for (let i = 0; i < HP_DEFAULT; i++) {
            const heart = atlasCreateImage(this.scene, 10 + i * 10, 10, this.processedTextures.heart_full);
            heart.setOrigin(0, 0);
            heart.setDepth(200); // UI layer
            heart.setScrollFactor(0); // Fixed to camera
            this.heartSprites.push(heart);
        }

        // Bubble sprites (positioned at top-right, all hidden initially)
        const screenW = this.scene.cameras.main.width;
        for (let i = 0; i < BUBBLE_COUNT; i++) {
            // Position from right edge: rightmost bubble (i=9) near right margin
            const x = screenW - 108 + i * 10;
            const bubble = atlasCreateImage(this.scene, x, 10, this.processedTextures.bubble_full);
            bubble.setOrigin(0, 0);
            bubble.setDepth(200); // UI layer
            bubble.setScrollFactor(0); // Fixed to camera
            bubble.setVisible(false); // Hidden until player is underwater
            this.bubbleSprites.push(bubble);
        }

        // Sword sprite (hidden until holding)
        // Sword BMP faces LEFT by default, so we flip for RIGHT facing
        this.swordSprite = atlasCreateImage(this.scene, 0, 0, this.processedTextures.sword);
        this.swordSprite.setOrigin(0, 0);
        this.swordSprite.setDepth(105); // Above local player
        this.swordSprite.setVisible(false);

        // Equipment slot box: 10x10 white border with 8x8 black interior
        // Positioned 6px from top-right corner (reuses screenW from bubble setup)
        const boxX = screenW - 6 - 10; // 6px buffer from right edge
        const boxY = 6; // 6px buffer from top edge
        this.equipmentBox = this.scene.add.graphics();
        this.equipmentBox.setScrollFactor(0);
        this.equipmentBox.setDepth(200);
        // White border (10x10)
        this.equipmentBox.fillStyle(0xFFFFFF, 1);
        this.equipmentBox.fillRect(boxX, boxY, 10, 10);
        // Black interior (8x8)
        this.equipmentBox.fillStyle(0x000000, 1);
        this.equipmentBox.fillRect(boxX + 1, boxY + 1, 8, 8);

        // Version label (top-left corner, micro font)
        const version = this.scene.add.bitmapText(1, 2, MICRO_FONT_KEY, __BUILD_VERSION__);
        version.setDepth(200);
        version.setScrollFactor(0);
        version.setAlpha(0.4);
    }

    /**
     * Update heart textures to reflect current HP.
     */
    updateHeartDisplay(hp: number, maxHp: number = HP_DEFAULT): void {
        const maxHearts = Math.max(hp, maxHp);

        // Add more heart sprites if needed
        while (this.heartSprites.length < maxHearts) {
            const i = this.heartSprites.length;
            const heart = atlasCreateImage(this.scene, 10 + i * 10, 10, this.processedTextures.heart_empty);
            heart.setOrigin(0, 0);
            heart.setDepth(200);
            heart.setScrollFactor(0);
            this.heartSprites.push(heart);
        }

        // Update heart textures
        for (let i = 0; i < this.heartSprites.length; i++) {
            if (i < hp) {
                atlasApplyTexture(this.scene, this.heartSprites[i], this.processedTextures.heart_full);
                this.heartSprites[i].setVisible(true);
            } else if (i < maxHearts) {
                atlasApplyTexture(this.scene, this.heartSprites[i], this.processedTextures.heart_empty);
                this.heartSprites[i].setVisible(true);
            } else {
                this.heartSprites[i].setVisible(false);
            }
        }
    }

    /**
     * Update bubble display to reflect current air ticks.
     * Bubbles are only visible when the player is underwater (airTicks < MAX_AIR_TICKS).
     * 10 bubbles, each with 4 stages (full → shrink1 → shrink2 → pop), 1 stage per tick.
     * Bubbles deplete left to right.
     */
    updateBubbleDisplay(airTicks: number): void {
        const shouldShow = airTicks < MAX_AIR_TICKS;

        if (!shouldShow) {
            // Hide all bubbles when at full air
            if (this.bubblesVisible) {
                for (const bubble of this.bubbleSprites) {
                    bubble.setVisible(false);
                }
                this.bubblesVisible = false;
            }
            return;
        }

        this.bubblesVisible = true;

        const ticksUsed = MAX_AIR_TICKS - airTicks;
        const completedBubbles = Math.floor(ticksUsed / TICKS_PER_BUBBLE);
        const stageInCurrent = ticksUsed % TICKS_PER_BUBBLE;

        for (let i = 0; i < BUBBLE_COUNT; i++) {
            const sprite = this.bubbleSprites[i];
            if (i < completedBubbles) {
                // This bubble has fully popped — hide it
                sprite.setVisible(false);
            } else if (i === completedBubbles) {
                // This bubble is currently depleting
                sprite.setVisible(true);
                switch (stageInCurrent) {
                    case 0: atlasApplyTexture(this.scene, sprite, this.processedTextures.bubble_full); break;
                    case 1: atlasApplyTexture(this.scene, sprite, this.processedTextures.bubble_shrink1); break;
                    case 2: atlasApplyTexture(this.scene, sprite, this.processedTextures.bubble_shrink2); break;
                    case 3: atlasApplyTexture(this.scene, sprite, this.processedTextures.bubble_pop); break;
                }
            } else {
                // This bubble is still full
                sprite.setVisible(true);
                atlasApplyTexture(this.scene, sprite, this.processedTextures.bubble_full);
            }
        }
    }

    /**
     * Update the equipment slot display to show the currently equipped item.
     */
    updateEquipmentDisplay(equippedItem: TileType | null): void {
        if (equippedItem === this.currentEquippedItem) return;
        this.currentEquippedItem = equippedItem;

        // Remove old sprite
        if (this.equipmentSprite) {
            this.equipmentSprite.destroy();
            this.equipmentSprite = null;
        }

        if (equippedItem === null) return;

        // Map equipment type to texture — use placeholder textures for tools
        let texture: string;
        switch (equippedItem) {
            case TileType.IRON_SWORD:
                texture = this.processedTextures.iron_sword;
                break;
            case TileType.WOODEN_SHIELD:
                texture = this.processedTextures.wooden_shield;
                break;
            case TileType.STONE_CLUB:
                texture = this.processedTextures.stone_club;
                break;
            default: {
                // Tool items use their placeholder textures
                const placeholderKey = `placeholder_${equippedItem}`;
                if (this.scene.textures.exists(placeholderKey)) {
                    texture = placeholderKey;
                } else {
                    return;
                }
                break;
            }
        }

        const screenW = this.scene.cameras.main.width;
        const spriteX = screenW - 6 - 10 + 1; // Inside the white border
        const spriteY = 6 + 1;
        this.equipmentSprite = atlasCreateImage(this.scene, spriteX, spriteY, texture);
        this.equipmentSprite.setOrigin(0, 0);
        this.equipmentSprite.setDepth(200);
        this.equipmentSprite.setScrollFactor(0);
    }

    /**
     * Render the local player's sword/club (or hide it).
     */
    renderLocalSword(
        isHolding: boolean,
        playerX: number,
        playerY: number,
        facing: number,
        isCrouching: boolean,
        offsetX: number,
        offsetY: number,
        equippedItem?: number,
    ): void {
        if (isHolding) {
            // Use club texture if ILO_IJO, otherwise sword
            if (equippedItem === TileType.ILO_IJO) {
                const clubKey = `placeholder_${TileType.ILO_IJO}`;
                if (this.scene.textures.exists(clubKey)) {
                    atlasApplyTexture(this.scene, this.swordSprite, clubKey);
                }
            } else {
                atlasApplyTexture(this.scene, this.swordSprite, this.processedTextures.sword);
            }
            const swordWorldX = playerX + facing * SWORD_OFFSET;
            const swordWorldY = playerY + (isCrouching ? 0 : 1); // At chest height
            const swordScreenX = swordWorldX * TILE_SIZE + offsetX;
            const swordScreenY = swordWorldY * TILE_SIZE + offsetY;
            this.swordSprite.setPosition(swordScreenX, swordScreenY);
            this.swordSprite.setFlipX(facing === 1); // Sword BMP faces LEFT, flip for RIGHT
            this.swordSprite.setVisible(true);
        } else {
            this.swordSprite.setVisible(false);
        }
    }

    /**
     * Render another player's sword (or hide it).
     */
    renderOtherPlayerSword(
        id: string,
        isHolding: boolean,
        playerX: number,
        playerY: number,
        facing: number,
        isCrouching: boolean,
        offsetX: number,
        offsetY: number,
    ): void {
        let swordSprite = this.otherPlayerSwords.get(id);

        if (isHolding) {
            if (!swordSprite) {
                swordSprite = atlasCreateImage(this.scene, 0, 0, this.processedTextures.sword);
                swordSprite.setOrigin(0, 0);
                swordSprite.setDepth(55); // Same depth as held blocks
                this.otherPlayerSwords.set(id, swordSprite);
            }
            const swordWorldX = playerX + facing * SWORD_OFFSET;
            const swordWorldY = playerY + (isCrouching ? 0 : 1);
            const swordScreenX = swordWorldX * TILE_SIZE + offsetX;
            const swordScreenY = swordWorldY * TILE_SIZE + offsetY;
            swordSprite.setPosition(swordScreenX, swordScreenY);
            swordSprite.setFlipX(facing === 1); // Sword BMP faces LEFT, flip for RIGHT
            swordSprite.setVisible(true);
        } else if (swordSprite) {
            swordSprite.setVisible(false);
        }
    }

    /**
     * Remove a specific player's sword sprite (on disconnect).
     */
    removePlayer(id: string): void {
        const swordSprite = this.otherPlayerSwords.get(id);
        if (swordSprite) {
            swordSprite.destroy();
            this.otherPlayerSwords.delete(id);
        }
    }

    /**
     * Set the server load level indicator.
     * 0 = healthy (hidden), 1 = degraded (yellow blink), 2 = critical (red blink)
     */
    setServerLoad(level: number): void {
        this.serverLoadLevel = level;
        if (!this.serverLoadDot) {
            this.serverLoadDot = this.scene.add.graphics();
            this.serverLoadDot.setDepth(200);
            this.serverLoadDot.setScrollFactor(0);
        }
        if (level === 0) {
            this.serverLoadDot.setVisible(false);
        } else {
            this.serverLoadDot.setVisible(true);
        }
    }

    /**
     * Update the server load blink animation. Call from scene update().
     */
    updateLoadIndicator(time: number): void {
        if (!this.serverLoadDot || this.serverLoadLevel === 0) return;
        // Blink: on for 500ms, off for 500ms
        const blink = Math.floor(time / 500) % 2 === 0;
        this.serverLoadDot.clear();
        if (blink) {
            const color = this.serverLoadLevel >= 2 ? 0xff3333 : 0xffcc33;
            // Draw small dot above the hearts, top-left
            this.serverLoadDot.fillStyle(color, 0.9);
            this.serverLoadDot.fillCircle(5, 5, 3);
        }
    }

    /**
     * Destroy all combat HUD sprites (on teleport/disconnect).
     */
    clear(): void {
        for (const [, sprite] of this.otherPlayerSwords) {
            sprite.destroy();
        }
        this.otherPlayerSwords.clear();
    }
}
